import { useCallback, useEffect, useMemo, useState } from "react";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import {
  useTagsWatchlistsCRUD,
  type TagsWatchlist,
} from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { validateKeywordsSize, MAX_KEYWORDS_COUNT, MAX_KEYWORDS_TOTAL_CHARS } from "@/pages/PortfolioBuilder/keywordLimits";
import { parseCsvKeywords, parseCsvTickers } from "@/pages/PortfolioBuilder/parsePastedList";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { getTagsByIds } from "@/shared/api/newsquawk-headlines";
import type { Portfolio } from "@/types/Portfolio";
import { emptyNotifications, type FilterNotifications } from "@/types/notifications";
import toast from "react-hot-toast";
import { trackFeatureUsage } from "@/utils/gtm";

type Category = { title: string; id: number | null };

export type WatchlistCreatorMode = "ticker" | "keyword" | "tag";

export interface UseWatchlistCreatorOptions {
  mode: WatchlistCreatorMode;
  /** Existing portfolio to edit (omit for create mode) */
  editingPortfolio?: Portfolio | null;
  /** Existing tag watchlist to edit (tag mode only; omit for create mode) */
  editingTagWatchlist?: TagsWatchlist | null;
  /** Pre-fill for CREATE mode (concierge "describe it" flow) — seeds the
   *  form without entering edit mode; saving still creates a new resource. */
  draft?: {
    name?: string;
    tickers?: string[];
    keywords?: string[];
    tags?: string[];
  } | null;
  onCreated?: () => void;
  onDeleted?: () => void;
}

// Synchronously hydrate selectedTags from a list of tag IDs using menuCategories;
// unknown IDs get a placeholder title that callers can refine via getTagsByIds.
function hydrateSelectedTags(tagIds: string[]): { initial: Category[]; unknownIds: string[] } {
  const initial: Category[] = [];
  const unknownIds: string[] = [];
  for (const tagId of tagIds) {
    let found = false;
    for (const cat of menuCategories) {
      const item = cat.items.find((i) => String(i.id) === tagId);
      if (item) {
        initial.push({ title: item.title, id: item.id });
        found = true;
        break;
      }
    }
    if (!found) {
      initial.push({ title: `Tag #${tagId}`, id: Number(tagId) });
      unknownIds.push(tagId);
    }
  }
  return { initial, unknownIds };
}

export function useWatchlistCreator(options: UseWatchlistCreatorOptions) {
  const { createMutation, updateMutation, deleteMutation, portfoliosQuery } = usePortfoliosCRUD();
  const portfolios = portfoliosQuery.data || [];
  const { createMutation: tagCreateMutation, updateMutation: tagUpdateMutation, deleteMutation: tagDeleteMutation } = useTagsWatchlistsCRUD();

  const isEditing = !!options.editingPortfolio || !!options.editingTagWatchlist;
  const editId = options.editingPortfolio?.id ?? options.editingTagWatchlist?.id;

  const initialSelectedTags = useMemo(
    () => (options.editingTagWatchlist ? hydrateSelectedTags(options.editingTagWatchlist.tags).initial : []),
    [options.editingTagWatchlist]
  );

  // Form state — pre-populated when editing
  const [name, setName] = useState(
    options.editingPortfolio?.name ?? options.editingTagWatchlist?.name ?? ""
  );
  const [tickers, setTickers] = useState<string[]>(options.editingPortfolio?.tickers ?? []);
  const [keywords, setKeywords] = useState<string[]>(options.editingPortfolio?.keywords ?? []);
  const [selectedTags, setSelectedTags] = useState<Category[]>(initialSelectedTags);
  const [color, setColor] = useState(options.editingPortfolio?.color ?? "#FF0000");
  const [priority, setPriority] = useState(options.editingPortfolio?.priority ?? 1);
  const [notifications, setNotifications] = useState<FilterNotifications>(
    options.editingPortfolio?.notifications ??
      options.editingTagWatchlist?.notifications ?? emptyNotifications()
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form to defaults (or to editing portfolio/tag-watchlist values)
  const reset = useCallback(() => {
    const p = options.editingPortfolio;
    const t = options.editingTagWatchlist;
    if (p) {
      setName(p.name);
      setTickers(p.tickers);
      setKeywords(p.keywords ?? []);
      setSelectedTags([]);
      setColor(p.color);
      setPriority(p.priority);
      setNotifications(p.notifications);
    } else if (t) {
      setName(t.name);
      setTickers([]);
      setKeywords([]);
      setSelectedTags(hydrateSelectedTags(t.tags).initial);
      setColor("#FF0000");
      setPriority(1);
      setNotifications(t.notifications ?? emptyNotifications());
    } else if (options.draft) {
      setName(options.draft.name ?? "");
      setTickers(options.draft.tickers ?? []);
      setKeywords(options.draft.keywords ?? []);
      setSelectedTags(
        options.draft.tags ? hydrateSelectedTags(options.draft.tags).initial : []
      );
      setColor("#FF0000");
      setPriority(1);
      setNotifications(emptyNotifications());
    } else {
      setName("");
      setTickers([]);
      setKeywords([]);
      setSelectedTags([]);
      setColor("#FF0000");
      setPriority(1);
      setNotifications(emptyNotifications());
    }
    setErrors({});
    setImportedWatchlistIds(new Set());
  }, [options.editingPortfolio, options.editingTagWatchlist, options.draft]);

  // Clear all form content (keeps name)
  const clearAll = useCallback(() => {
    setTickers([]);
    setKeywords([]);
    setSelectedTags([]);
    setImportedWatchlistIds(new Set());
    setErrors({});
  }, []);

  // Re-populate form when the editing target or draft changes
  useEffect(() => {
    reset();
  }, [options.editingPortfolio, options.editingTagWatchlist, options.draft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve display names for any tag IDs not present in menuCategories
  useEffect(() => {
    const t = options.editingTagWatchlist;
    if (!t) return;
    const { unknownIds } = hydrateSelectedTags(t.tags);
    if (unknownIds.length === 0) return;

    let cancelled = false;
    getTagsByIds(unknownIds.join(" "))
      .then((tagDetails) => {
        if (cancelled || !tagDetails) return;
        setSelectedTags((prev) =>
          prev.map((cat) => {
            const tagId = String(cat.id);
            const detail = tagDetails[tagId];
            if (!detail) return cat;
            const primary = detail.labels?.find((l) => l.primary);
            const resolved = primary?.name || detail.labels?.[0]?.name;
            return resolved ? { ...cat, title: resolved } : cat;
          })
        );
      })
      .catch((err) => {
        console.error("Failed to fetch tag names:", err);
      });

    return () => {
      cancelled = true;
    };
  }, [options.editingTagWatchlist]);

  // Track which Newsquawk watchlists are imported (for clean removal)
  const [importedWatchlistIds, setImportedWatchlistIds] = useState<Set<number>>(new Set());

  const keywordsSize = validateKeywordsSize(keywords);

  // Import tickers/keywords from a Newsquawk watchlist
  const importFromWatchlist = useCallback((portfolioId: number) => {
    const portfolio = portfolios.find((p) => p.id === portfolioId);
    if (!portfolio) return;

    if (options.mode === "ticker") {
      const newTickers = portfolio.tickers.filter((t) => !tickers.includes(t));
      setTickers((prev) => [...prev, ...newTickers]);
    } else if (options.mode === "keyword") {
      const newKeywords = (portfolio.keywords ?? []).filter((k) => !keywords.includes(k));
      setKeywords((prev) => [...prev, ...newKeywords]);
    }

    setImportedWatchlistIds((prev) => new Set(prev).add(portfolioId));
  }, [portfolios, tickers, keywords, options.mode]);

  // Remove imported tickers/keywords from a Newsquawk watchlist
  const removeImportedWatchlist = useCallback((portfolioId: number) => {
    const portfolio = portfolios.find((p) => p.id === portfolioId);
    if (!portfolio) return;

    if (options.mode === "ticker") {
      const toRemove = new Set(portfolio.tickers);
      setTickers((prev) => prev.filter((t) => !toRemove.has(t)));
    } else if (options.mode === "keyword") {
      const toRemove = new Set(portfolio.keywords ?? []);
      setKeywords((prev) => prev.filter((k) => !toRemove.has(k)));
    }

    setImportedWatchlistIds((prev) => {
      const next = new Set(prev);
      next.delete(portfolioId);
      return next;
    });
  }, [portfolios, options.mode]);

  // Toggle a Newsquawk watchlist import
  const toggleWatchlistImport = useCallback((portfolioId: number) => {
    if (importedWatchlistIds.has(portfolioId)) {
      removeImportedWatchlist(portfolioId);
    } else {
      importFromWatchlist(portfolioId);
    }
  }, [importedWatchlistIds, importFromWatchlist, removeImportedWatchlist]);

  // Toggle a tag
  const toggleTag = useCallback((tag: Category) => {
    setSelectedTags((prev) => {
      const exists = prev.some((t) => t.id === tag.id);
      return exists ? prev.filter((t) => t.id !== tag.id) : [...prev, tag];
    });
    setErrors((e) => ({ ...e, tags: "" }));
  }, []);

  // Manual ticker/keyword add
  const addTicker = useCallback((value: string) => {
    const trimmed = value.trim().toUpperCase();
    if (trimmed && !tickers.includes(trimmed)) {
      setTickers((prev) => [...prev, trimmed]);
      setErrors((e) => ({ ...e, tickers: "" }));
    }
  }, [tickers]);

  const addTickers = useCallback((values: string[]) => {
    if (values.length === 0) return;
    setTickers((prev) => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const v of values) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    });
    setErrors((e) => ({ ...e, tickers: "" }));
  }, []);

  const removeTicker = useCallback((value: string) => {
    setTickers((prev) => prev.filter((t) => t !== value));
  }, []);

  const addKeyword = useCallback((value: string) => {
    const trimmed = value.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      setKeywords((prev) => [...prev, trimmed]);
    }
  }, [keywords]);

  const addKeywords = useCallback((values: string[]) => {
    if (values.length === 0) return;
    setKeywords((prev) => {
      const seen = new Set(prev);
      const next = [...prev];
      for (const v of values) {
        if (!seen.has(v)) {
          seen.add(v);
          next.push(v);
        }
      }
      return next;
    });
  }, []);

  const removeKeyword = useCallback((value: string) => {
    setKeywords((prev) => prev.filter((k) => k !== value));
  }, []);

  // CSV upload for tickers
  const handleCsvUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      addTickers(parseCsvTickers(text));
    };
    reader.readAsText(file);
  }, [addTickers]);

  // CSV upload for keywords
  const handleKeywordCsvUpload = useCallback((file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      addKeywords(parseCsvKeywords(text));
    };
    reader.readAsText(file);
  }, [addKeywords]);

  // Submit (create or update)
  const handleSubmit = useCallback(async () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = "Name is required";

    if (options.mode === "ticker" && tickers.length === 0) errs.tickers = "At least one ticker is required";
    if (options.mode === "keyword" && keywords.length === 0) errs.keywords = "At least one keyword is required";
    if (options.mode === "keyword" && keywords.length > 0 && !keywordsSize.ok) errs.keywords = keywordsSize.error ?? "Keyword limit exceeded";
    if (options.mode === "tag" && selectedTags.length === 0) errs.tags = "At least one tag is required";

    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setIsSubmitting(true);
    try {
      if (options.mode === "tag") {
        if (isEditing && editId != null) {
          await tagUpdateMutation.mutateAsync({
            id: editId,
            data: {
              name: name.trim(),
              tags: selectedTags.map((t) => String(t.id)),
              notifications,
            },
          });
          toast.success("Tag watchlist updated");
        } else {
          await tagCreateMutation.mutateAsync({
            name: name.trim(),
            tags: selectedTags.map((t) => String(t.id)),
            notifications,
          });
          toast.success("Tag watchlist created");
          trackFeatureUsage({
            feature_name: "watchlist",
            feature_action: "create",
            feature_detail: "tag",
          });
        }
      } else if (isEditing && editId != null) {
        await updateMutation.mutateAsync({
          ...(options.editingPortfolio as Portfolio),
          name: name.trim(),
          tickers: options.mode === "ticker" ? tickers : (options.editingPortfolio?.tickers ?? []),
          keywords: options.mode === "keyword" ? keywords : (options.editingPortfolio?.keywords ?? []),
          color,
          priority,
          notifications,
        });
        toast.success("Watchlist updated");
      } else {
        const newId = Math.max(0, ...portfolios.map((p) => p.id)) + 1;
        await createMutation.mutateAsync({
          id: newId,
          name: name.trim(),
          tickers: options.mode === "ticker" ? tickers : [],
          keywords: options.mode === "keyword" ? keywords : [],
          color,
          priority,
          notifications,
          customizations: { includeRelatedHeadlines: false },
          owner_type: "USER",
        } as Portfolio);
        toast.success("Watchlist created");
        trackFeatureUsage({
          feature_name: "watchlist",
          feature_action: "create",
          feature_detail: options.mode,
        });
      }
      options.onCreated?.();
    } catch {
      toast.error(isEditing ? "Failed to update watchlist" : "Failed to create watchlist");
    } finally {
      setIsSubmitting(false);
    }
  }, [name, tickers, keywords, selectedTags, color, priority, notifications, options.mode, keywordsSize, portfolios, createMutation, updateMutation, tagCreateMutation, tagUpdateMutation, isEditing, editId, options.editingPortfolio, options.editingTagWatchlist, options.onCreated]);

  // Delete (edit mode only)
  const handleDelete = useCallback(async () => {
    if (!isEditing || editId == null) return;
    setIsSubmitting(true);
    try {
      if (options.mode === "tag") {
        await tagDeleteMutation.mutateAsync(editId);
        toast.success("Tag watchlist deleted");
      } else {
        await deleteMutation.mutateAsync(String(editId));
        toast.success("Watchlist deleted");
      }
      options.onDeleted?.();
    } catch {
      toast.error("Failed to delete watchlist");
    } finally {
      setIsSubmitting(false);
    }
  }, [isEditing, editId, options.mode, deleteMutation, tagDeleteMutation, options.onDeleted]);

  return {
    // Form state
    name, setName,
    tickers, keywords, selectedTags,
    color, setColor,
    priority, setPriority,
    notifications, setNotifications,
    errors, isSubmitting,

    // Actions
    addTicker, addTickers, removeTicker,
    addKeyword, addKeywords, removeKeyword,
    toggleTag,
    handleCsvUpload,
    handleKeywordCsvUpload,
    toggleWatchlistImport,
    importedWatchlistIds,
    handleSubmit,
    handleDelete,
    reset,
    clearAll,
    isEditing,

    // Derived
    keywordsSize,
    MAX_KEYWORDS_COUNT,
    MAX_KEYWORDS_TOTAL_CHARS,
    mode: options.mode,
    portfolios,
  };
}

export type UseWatchlistCreatorReturn = ReturnType<typeof useWatchlistCreator>;
